import React, { useState, useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, useWindowDimensions } from 'react-native';
import { Spacing, FontSize, BorderRadius } from '../theme/colors';
import { HomeworkScreen } from './HomeworkScreen';
import { ProjectScreen } from './ProjectScreen';
import { ExamResultsScreen } from './ExamResultsScreen';
import { AssessmentResultsScreen } from './AssessmentResultsScreen';
import { TimetableScreen } from './TimetableScreen';
import { WebPageShell } from '../components/common/WebPageShell';
import { useTheme, ThemeColors } from '../context/ThemeContext';

type Tab = 'Homework' | 'Projects' | 'Exams' | 'Assessments' | 'Timetable';

export function AcademicsScreen() {
  const [activeTab, setActiveTab] = useState<Tab>('Timetable');
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const { width } = useWindowDimensions();
  const isMobile = width < 768;

  return (
    <WebPageShell title="Academics" contentStyle={{ maxWidth: 900 }}>
      <View style={{ flexGrow: 1 }}>
        {/* <Text style={styles.headerTitle}>Academics</Text> */}

        {isMobile ? (
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            style={styles.tabContainerMobileScroll}
            contentContainerStyle={styles.tabContainerMobile}
          >
            {(['Timetable', 'Homework', 'Projects', 'Exams', 'Assessments'] as Tab[]).map((tab) => {
              const isActive = activeTab === tab;
              return (
                <TouchableOpacity
                  key={tab}
                  style={[styles.tabButton, styles.tabButtonMobile, isActive && styles.activeTabButton]}
                  onPress={() => setActiveTab(tab)}>
                  <Text style={[styles.tabText, isActive && styles.activeTabText]}>
                    {tab}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        ) : (
          <View style={styles.tabContainer}>
            {(['Timetable', 'Homework', 'Projects', 'Exams', 'Assessments'] as Tab[]).map((tab) => {
              const isActive = activeTab === tab;
              return (
                <TouchableOpacity
                  key={tab}
                  style={[styles.tabButton, isActive && styles.activeTabButton]}
                  onPress={() => setActiveTab(tab)}>
                  <Text style={[styles.tabText, isActive && styles.activeTabText]}>
                    {tab}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
        )}

        <View style={styles.content}>
          {activeTab === 'Homework' && <HomeworkScreen isNested />}
          {activeTab === 'Projects' && <ProjectScreen isNested />}
          {activeTab === 'Exams' && <ExamResultsScreen isNested />}
          {activeTab === 'Assessments' && <AssessmentResultsScreen isNested />}
          {activeTab === 'Timetable' && <TimetableScreen isNested />}
        </View>
      </View>
    </WebPageShell>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  headerTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: colors.textPrimary,
    paddingHorizontal: Spacing.md,
    paddingTop: Spacing.md,
    paddingBottom: Spacing.sm,
  },
  tabContainer: {
    flexDirection: 'row',
    backgroundColor: colors.border,
    borderRadius: BorderRadius.md,
    marginHorizontal: Spacing.md,
    marginBottom: Spacing.md,
    padding: 4,
  },
  tabButton: {
    flex: 1,
    paddingVertical: 8,
    alignItems: 'center',
    borderRadius: BorderRadius.sm,
  },
  tabContainerMobileScroll: {
    marginHorizontal: Spacing.md,
    marginBottom: Spacing.md,
  },
  tabContainerMobile: {
    backgroundColor: colors.border,
    borderRadius: BorderRadius.md,
    padding: 4,
    gap: 4,
  } as any,
  tabButtonMobile: {
    flex: undefined,
    paddingHorizontal: 16,
  },
  activeTabButton: {
    backgroundColor: colors.card,
    shadowColor: colors.shadow,
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  tabText: {
    fontSize: FontSize.sm,
    fontWeight: '600',
    color: colors.textSecondary,
  },
  activeTabText: {
    color: colors.primary,
    fontWeight: '700',
  },
  content: {
    flexGrow: 1,
  },
});
